import { Grid, Menu, MenuItem, ListItemText, ListItemIcon, Box, Typography, makeStyles, FormControlLabel, Switch, Link } from '@material-ui/core';
import { ArrowDropDown, Clear, Payment, PlaylistAddCheck, CloudDownload, MoneyOff } from '@material-ui/icons';
import { Button, Toolbar, ConfirmationMenuItem, SelectOption, SelectField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { ProtectiveAdvancesSearchBar } from '../protectiveAdvancesSearchBar';
import { CounterPartyExpensesTab, PayableReceivablesTypes, PAYABLES_TABS, ProtectiveAdvanceModalType } from '../../types/types';
import { MarkAsPaidModal } from './markAsPaidModal';
import { useBaseStore } from '@roc/feature-app-core';
import { ToolbarInfoItem } from '../toolbarInfoItem';
import { useProtectiveAdvancesStore } from '../../hooks/useProtectiveAdvancesStore';
import { ProtectiveAdvanceModal } from '../loan/protectiveAdvanceModal';

export const TYPES_OF_FEE: Array<SelectOption> = [
  { label: 'Appraisal Fee', value: 'Appraisal Fee' },
  { label: 'BPO Fee', value: 'BPO Fee' },
  { label: 'Courier Fee', value: 'Courier Fee' },
  { label: 'Delinquent Property Tax', value: 'Delinquent Property Tax' },
  { label: 'Force Placed Insurance', value: 'Force Placed Insurance' },
  { label: 'Legal Fee', value: 'Legal Fee' },
  { label: 'Miscellaneous Fee', value: 'Miscellaneous Fee' },
  { label: 'Property Preservation Fee', value: 'Property Preservation Fee' },
  { label: 'Real Estate Tax', value: 'Real Estate Tax' },
  { label: 'Recording Fee', value: 'Recording Fee' },
  { label: 'REO Insurance', value: 'REO Insurance' },
  { label: 'Title Fee', value: 'Title Fee' },
  { label: 'Sheriffs Fee', value: 'Sheriffs Fee' },
  { label: 'Town Fee', value: 'Town Fee' },
  { label: 'CEMA Processing Fee', value: 'CEMA Processing Fee' },
  { label: 'Personal Guaranty Fee', value: 'Personal Guaranty Fee' },
];

const useStyles = makeStyles(theme => ({
  content: {
    fontWeight: 'bold',
    fontSize: '15px'
  },
  label: {
    fontSize: '13px',
  },
  labelBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  labelBoxCount: {
    marginRight: '25px',
    marginLeft: '25px',
  },
  labelBoxAmount: {
    marginRight: '50px',
    marginLeft: '25px',
  },
  dataPoint: {
    marginTop: '5px',
    textAlign: 'center',
  },
  containerBox: {
    display: 'flex',
  },
  infoItemsBox: {
    marginRight: '40px',
    marginLeft: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  },
}));

export const PayablesToolbar = observer(({ counterPartyExpenseStore }) => {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [showBulkActionsMenu, setShowBulkActionsMenu] = useState(false);
  const [payableRefNumber, setPayableRefNumber] = useState<string>('');
  const { tab, entity } = counterPartyExpenseStore;
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [showProtectiveAdvanceModal, setShowProtectiveAdvanceModal] = useState(false);
  const { globalStore } = useBaseStore();
  const { protectiveAdvancesStore } = useProtectiveAdvancesStore();

  const handleOpenProtectiveAdvanceModal = () => {
    protectiveAdvancesStore.protectiveAdvanceFormStore.setType(ProtectiveAdvanceModalType.CREATE);
    setShowProtectiveAdvanceModal(true);
  };

  const closeMenu = () => {
    setShowBulkActionsMenu(false);
  };

  const closeMarkAsPaidModal = () => {
    setPayableRefNumber('');
    setShowMarkAsPaidModal(false);
  };

  const openMarkAsPaidModal = () => {
    if (counterPartyExpenseStore.recordsIds && counterPartyExpenseStore.recordsIds.length > 0) {
      setShowMarkAsPaidModal(true);
    } else {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please select an item from the table.',
      });
    }

  }

  const handleMarkAsPaidConfirm = async () => {
    await counterPartyExpenseStore.markMultipleAsPaid(payableRefNumber);
    counterPartyExpenseStore.resetToolbarInfo();
    counterPartyExpenseStore.setShowSelectedRecords(false);
    counterPartyExpenseStore.counterPartyExpenseGridStore.resetAndFetchGridData();
    closeMarkAsPaidModal();
  }

  const downloadDelinquentTaxReport = async () => {
    if (counterPartyExpenseStore.recordsIds && counterPartyExpenseStore.recordsIds.length > 0) {
      await counterPartyExpenseStore.downloadTaxReport();
    } else {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please select an item from the table.',
      });
    }
  }

  const performAction = async (status) => {
    try {
      if (counterPartyExpenseStore.recordsIds && counterPartyExpenseStore.recordsIds.length > 0) {
        await counterPartyExpenseStore.performActions(status);
        counterPartyExpenseStore.resetToolbarInfo();
        counterPartyExpenseStore.setShowSelectedRecords(false);
        counterPartyExpenseStore.counterPartyExpenseGridStore.resetAndFetchGridData();
        if (status === PayableReceivablesTypes.PROCESSING) {
          globalStore.notificationStore.showSuccessNotification({
            message: <span>
              The vendor payment was created successfully.{" "}
              <Link component="button" onClick={() => protectiveAdvancesStore.setTab(CounterPartyExpensesTab.VENDOR_PAYMENT)}>
                Go to VENDOR-PAYMENTS
              </Link>
            </span>,
          });
        } else {
          globalStore.notificationStore.showSuccessNotification({
            message: 'Status updated successfully.'
          });
        }

      } else {
        globalStore.notificationStore.showErrorNotification({
          message: 'Please select an item from the table.',
        });
      }
    } catch (error) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating status.',
      });
    }
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Toolbar left={
          <>
            <Box alignItems="center" display="flex" pr={2}>
              <Typography style={{ paddingRight: '16px' }}>{"Status"}: </Typography>
              <SelectField
                testId={'typeOfFee'}
                fullWidth
                variant="outlined"
                options={PAYABLES_TABS}
                value={tab}
                onChange={value => {
                  counterPartyExpenseStore.setTab(value);
                }}
                required={true}
                style={{ width: '125px' }}
              />
            </Box>
            <Box>
              <ProtectiveAdvancesSearchBar
                counterPartyExpenseStore={counterPartyExpenseStore}
                label={'Vendor'}
              />
            </Box>
          </>
        }
        >
          <Box className={classes.containerBox}>
            <Box className={classes.infoItemsBox}>
              <ToolbarInfoItem
                value={counterPartyExpenseStore.recordsIds.length}
                label={'Count'}
              />
              <ToolbarInfoItem
                value={`${'$'}${counterPartyExpenseStore.totalAmount}`}
                label={'Amount'}
              />
            </Box>
            <Box style={{ textAlign: 'center', marginRight: '25px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={counterPartyExpenseStore.showSelectedRecords}
                    onChange={(e) => {
                      counterPartyExpenseStore.setShowSelectedRecords(!counterPartyExpenseStore.showSelectedRecords);
                      counterPartyExpenseStore.counterPartyExpenseGridStore.resetAndFetchGridData();

                    }}
                    color="primary"
                  />
                }
                label={"Show Selected"}
              />
            </Box>
            <Box style={{
              alignItems: 'center',
              justifyContent: 'center', textAlign: 'center', marginRight: '25px',
            }}>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={handleOpenProtectiveAdvanceModal}
                testId="add-protective-advance"
                style={{ marginRight: '8px' }}
              >
                Add Payable
              </Button>
            </Box>
            <Box
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                marginRight: '25px',
              }}
            >
              <Button
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={() => counterPartyExpenseStore.exportServicingDashboardData()}
                testId="open_menu"
                variant="contained"
                size="small"
                color="primary"
              >
                Export as Excel
              </Button>
            </Box>
            <Box style={{ textAlign: 'center' }}>
              <div ref={anchorRef} style={{ display: 'inline-block' }}>
                <Button
                  id="basic-button"
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={() => { setShowBulkActionsMenu(true) }}
                  testId="open_menu"
                  variant="contained"
                  size="small"
                  color="primary"
                >
                  Bulk Actions <ArrowDropDown style={{ marginRight: '-6px' }} />
                </Button>
                <Menu
                  id="basic-menu"
                  anchorEl={anchorRef.current}
                  open={showBulkActionsMenu}
                  onClose={closeMenu}
                  keepMounted={true}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  getContentAnchorEl={null}
                >
                  <ConfirmationMenuItem
                    testId={`approve-payable`}
                    onClick={() => { performAction(PayableReceivablesTypes.APPROVED) }}
                    confirmDialogProps={{
                      body: `Are you sure you want to Approve the selected records?`,
                    }}
                  >
                    <ListItemIcon>
                      <PlaylistAddCheck fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Approve</ListItemText>
                  </ConfirmationMenuItem>
                  <ConfirmationMenuItem
                    testId={`reject-payable`}
                    onClick={() => { performAction(PayableReceivablesTypes.REJECTED) }}
                    confirmDialogProps={{
                      body: `Are you sure you want to Reject the selected records?`,
                    }}
                  >
                    <ListItemIcon>
                      <Clear fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Reject</ListItemText>
                  </ConfirmationMenuItem>
                  <MenuItem onClick={openMarkAsPaidModal}>
                    <ListItemIcon>
                      <Payment fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Mark as Paid</ListItemText>
                  </MenuItem>
                  <ConfirmationMenuItem
                    testId={`vendor-payment`}
                    onClick={() => { performAction(PayableReceivablesTypes.PROCESSING) }}
                    confirmDialogProps={{
                      body: `Are you sure you want to create vendor payment?`,
                    }}
                    disabled={!entity}
                  >
                    <ListItemIcon>
                      <PlaylistAddCheck fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Create Vendor Payment</ListItemText>
                  </ConfirmationMenuItem>
                  <ConfirmationMenuItem
                    testId={`credit memo`}
                    onClick={async () => {
                      await counterPartyExpenseStore.createCreditMemoFromPayables();
                      counterPartyExpenseStore.counterPartyExpenseGridStore.resetAndFetchGridData();
                    }}
                    confirmDialogProps={{
                      body: `Are you sure you want to create credit memos for the selected items?`,
                    }}
                    disabled={counterPartyExpenseStore.recordsIds.length <= 0}
                  >
                    <ListItemIcon>
                      <MoneyOff fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Create Credit Memo</ListItemText>
                  </ConfirmationMenuItem>
                  <MenuItem disabled={!entity} onClick={downloadDelinquentTaxReport}>
                    <ListItemIcon>
                      <CloudDownload fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Download Delinquent Tax Report</ListItemText>
                  </MenuItem>
                </Menu>
              </div>
            </Box>
          </Box>
        </Toolbar>
      </Grid>
      <MarkAsPaidModal
        payableRefNumber={payableRefNumber}
        setPayableRefNumber={setPayableRefNumber}
        showMarkAsPaidModal={showMarkAsPaidModal}
        closeMarkAsPaidModal={closeMarkAsPaidModal}
        handleMarkAsPaidConfirm={handleMarkAsPaidConfirm}
      />
      <ProtectiveAdvanceModal
        open={showProtectiveAdvanceModal}
        loanId={''}
        store={protectiveAdvancesStore.protectiveAdvanceFormStore}
        vendors={counterPartyExpenseStore.entityList || []}
        onClose={() => setShowProtectiveAdvanceModal(false)}
        onUpload={() => {
          counterPartyExpenseStore.counterPartyExpenseGridStore.resetAndFetchGridData();
          setShowProtectiveAdvanceModal(false);
        }}
        fromPayables={true}
      />
    </Grid>
  );
});
